import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocLink,
  DocNavFooter,
  DocCard,
} from "@/components/docs";
import { ApiEndpoint, CodeBlock, Parameter } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import { ExclamationTriangleIcon, DocumentTextIcon, ClockIcon } from "@heroicons/react/24/outline";

export default function ApiIncidentsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Incidents API" },
  ];

  const listIncidentsQueryParams: Parameter[] = [
    {
      name: "page",
      type: "integer",
      required: false,
      description: "Page number for pagination",
      default: "1",
      example: "2",
    },
    {
      name: "per_page",
      type: "integer",
      required: false,
      description: "Items per page (max 100)",
      default: "20",
      example: "50",
    },
    {
      name: "monitor_id",
      type: "string",
      required: false,
      description: "Filter by specific monitor ID",
      example: "507f1f77bcf86cd799439011",
    },
    {
      name: "resolved",
      type: "boolean",
      required: false,
      description: "Filter by resolution status",
      example: "false",
    },
  ];

  const pathParams: Parameter[] = [
    {
      name: "id",
      type: "string",
      required: true,
      description: "Incident ID",
      example: "507f1f77bcf86cd799439011",
    },
  ];

  const updateCommentBodyParams: Parameter[] = [
    {
      name: "comment",
      type: "string",
      required: true,
      description: "Comment text for the incident",
      example: "Database server ran out of memory due to traffic spike",
    },
  ];

  return (
    <DocLayout
      title="Incidents API - Uptime Monitor Documentation"
      description="API reference for managing and tracking incidents in Uptime Monitor"
      canonical="https://uptime-monitor.io/docs/api/incidents"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Incidents API"
        description="Track, manage, and comment on incidents when monitors detect issues."
      />

      <DocAlert variant="info" className="mb-8">
        <p>
          Incidents are automatically created when a monitor fails its checks according to the
          configured fail threshold. Each incident tracks the downtime period, affected regions, and
          failure reasons.
        </p>
        <p className="mt-2 text-sm">
          <strong>Note:</strong> Incidents are read-only except for adding comments. They are
          automatically resolved when the monitor returns to an up state.
        </p>
      </DocAlert>

      {/* Quick Reference */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Incident Lifecycle
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <DocCard className="p-4 text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Created</div>
            <div className={`text-sm ${docsColors.text.muted}`}>When fail threshold reached</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <ClockIcon className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Ongoing</div>
            <div className={`text-sm ${docsColors.text.muted}`}>Tracks downtime duration</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <DocumentTextIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Resolved</div>
            <div className={`text-sm ${docsColors.text.muted}`}>When monitor recovers</div>
          </DocCard>
        </div>

        <p className={`${docsColors.text.body}`}>
          Incidents provide a complete history of downtime events with detailed check results from
          all monitoring regions, helping you understand and diagnose issues.
        </p>
      </section>

      {/* List Incidents */}
      <ApiEndpoint
        method="GET"
        path="/api/incidents"
        description="Retrieve a paginated list of incidents with optional filtering."
        queryParameters={listIncidentsQueryParams}
        requestExample={`curl -X GET "https://api.uptime-monitor.io/api/incidents?resolved=false&per_page=10" \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "incidents": [
      {
        "id": "507f1f77bcf86cd799439011",
        "is_resolved": false,
        "started_at": 1640995200,
        "duration": "Ongoing",
        "user_comment": "",
        "reason": "Connection timeout",
        "check_results": {
          "us-east-1": {
            "checker_ip": "54.123.45.67",
            "ts": 1640995200,
            "time": 30000,
            "status": "down",
            "error": {
              "reason": "Connection timeout",
              "http_check_details": null
            },
            "timestamp": 1640995200
          }
        }
      }
    ],
    "pagination": {
      "page": 1,
      "per_page": 10,
      "total": 45,
      "total_pages": 5,
      "has_next": true,
      "has_prev": false
    }
  }
}`}
      />

      {/* Get Incident */}
      <ApiEndpoint
        method="GET"
        path="/api/incidents/:id"
        description="Retrieve detailed information about a specific incident."
        parameters={pathParams}
        requestExample={`curl -X GET https://api.uptime-monitor.io/api/incidents/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "incident": {
      "id": "507f1f77bcf86cd799439011",
      "is_resolved": true,
      "started_at": 1640995200,
      "duration": "2h 15m",
      "user_comment": "Network issue at datacenter, resolved after provider fix",
      "reason": "503 Service Unavailable",
      "check_results": {
        "us-east-1": {
          "checker_ip": "54.123.45.67",
          "ts": 1640995200,
          "time": 523,
          "status": "down",
          "error": {
            "reason": "503 Service Unavailable",
            "http_check_details": {
              "status_code": 503,
              "headers": {
                "server": "nginx/1.21.0"
              }
            }
          },
          "timestamp": 1640995200
        }
      }
    }
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "",
            description: "Incident not found",
          },
          {
            status: 400,
            code: "",
            description: "Invalid incident ID format",
          },
        ]}
      />

      {/* Update Incident Comment */}
      <ApiEndpoint
        method="PUT"
        path="/api/incidents/:id/comment"
        description="Add or update a comment on an incident for post-mortem documentation."
        parameters={pathParams}
        bodyParameters={updateCommentBodyParams}
        requestExample={`curl -X PUT https://api.uptime-monitor.io/api/incidents/507f1f77bcf86cd799439011/comment \\
  -H "Authorization: Bearer um_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "comment": "Database server ran out of memory due to traffic spike. Increased memory allocation and implemented better caching."
  }'`}
        requestExampleLanguage="bash"
        responseExample={`{
  "status": "ok",
  "data": {
    "incident": {
      "id": "507f1f77bcf86cd799439011",
      "is_resolved": true,
      "started_at": 1640995200,
      "duration": "2h 15m",
      "user_comment": "Database server ran out of memory due to traffic spike. Increased memory allocation and implemented better caching.",
      "reason": "503 Service Unavailable",
      "check_results": {
        "us-east-1": {
          "checker_ip": "54.123.45.67",
          "ts": 1640995200,
          "time": 500,
          "status": "down",
          "error": {
            "reason": "503 Service Unavailable",
            "http_check_details": {
              "status_code": 503,
              "headers": {
                "server": "nginx/1.21.0"
              }
            }
          },
          "timestamp": 1640995200
        }
      }
    }
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "",
            description: "Incident not found",
          },
          {
            status: 400,
            code: "",
            description: "Invalid incident ID format",
          },
        ]}
      />

      {/* Check Result Structure */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Check Result Structure
        </h2>

        <DocCard className="p-6">
          <p className={`${docsColors.text.body} mb-4`}>
            Each region check result contains diagnostic information:
          </p>
          <CodeBlock language="json" title="Check Result Fields">
            {`{
  "checker_ip": "54.123.45.67",     // IP address of the checker
  "ts": 1640995200,                 // Timestamp of the check (Unix)
  "time": 523,                      // Response time in milliseconds
  "status": "down",                 // Check status: up, down, or degraded
  "error": {                        // Present only when status is not "up"
    "reason": "Connection timeout",  // Human-readable error description
    "http_check_details": {         // Additional details for HTTP checks
      "status_code": 503,
      "headers": {
        "server": "nginx/1.21.0"
      }
    }
  },
  "timestamp": 1640995200           // Unix timestamp (same as ts)
}`}
          </CodeBlock>
        </DocCard>
      </section>

      <DocNavFooter
        prev={{ href: "/docs/api/monitors", text: "Monitors API" }}
        next={{ href: "/docs/api/status-pages", text: "Status Pages API" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
